import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { sessionActions } from '../../../../../../store';
import { EVV_FALLBACK_REASONS } from '../../../../../constants';
import { buildEvvLocationPayload, getEvvGeolocation } from '../../../../utils';

import type { EVVFallbackReason } from '../../../../../types';
import type { SelectValueType } from '../../../../../../utils/types';
import type { UseEvvDesktopFallbackParams, UseEvvDesktopFallbackResult } from './types';

export function useEvvDesktopFallback({
  open,
  kind,
  sessionId,
}: UseEvvDesktopFallbackParams): UseEvvDesktopFallbackResult {
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const [fallbackChecked, setFallbackChecked] = useState(false);
  const [reason, setReason] = useState<EVVFallbackReason | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [locationError, setLocationError] = useState(false);

  useEffect(() => {
    if (!open) return;

    setFallbackChecked(false);
    setReason(null);
    setSubmitting(false);
    setLocationError(false);
  }, [open]);

  const toggleFallback = (checked: boolean) => {
    setFallbackChecked(checked);
    if (checked) return;

    setReason(null);
    setLocationError(false);
  };

  const selectReason = (value: SelectValueType) => {
    setReason(Object.values(EVV_FALLBACK_REASONS).find((item) => item === value) ?? null);
  };

  const submitFallback = async () => {
    if (!reason) return;

    setSubmitting(true);
    setLocationError(false);
    const location = await getEvvGeolocation();
    if ('error' in location) {
      setLocationError(true);
      setSubmitting(false);
      return;
    }

    const response = await dispatch(
      sessionActions.captureEvvDesktop({
        sessionId,
        kind,
        data: { reason, ...buildEvvLocationPayload(location.coords) },
        getAccessTokenSilently,
      }),
    );

    // On success the owner's status poll closes the dialog, so the button stays in its loading state.
    if (response.error) {
      toast.error(response.error.message);
      setSubmitting(false);
    }
  };

  return {
    fallbackChecked,
    reason,
    submitting,
    locationError,
    toggleFallback,
    selectReason,
    submitFallback,
  };
}
